/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Grid, TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/slices/snackbar';
import useAuth from 'hooks/useAuth';
import MainCard from '../MainCard';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Formik, Field, ErrorMessage } from 'formik';
import { ListingActions } from 'store/slices/listings';
import { findKeyInObject } from 'utils/findKeyInObjects';
import { DateTimePicker } from '@mui/x-date-pickers';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { formatDate } from 'views/dashboard/Listing/HelperFunction';

export default function ListingAnalytics({ historyRefresh, propertyId, hitCounters, hitCountersCurrent, isEdited }) {
    const dispatch = useDispatch();
    const { role, user } = useAuth();

    const { addListingAnalytics } = ListingActions;

    const validationSchema = Yup.object().shape({
        clientPortals: Yup.number().required('Client Portals is required!'),
        clientViews: Yup.number().required('Client Views is required!'),
        clientFavorites: Yup.number().required('Client Favorites is required!'),
        clientPossibilities: Yup.number().required('Client Possibilities is required!'),
        autoEmails: Yup.number().required('Auto Emails is required!'),
        prev14DayViews: Yup.number().required('Prev 14 Days Views is required!'),
        agentPublished: Yup.number().required('Agent Published is required!'),
        agentRejected: Yup.number().required('Agent Rejected is required!'),
        agentIDX: Yup.number().required('Agent IDX is required!'),
        agentViews: Yup.number().required('Agent Views is required!')
    });

    return (
        <MainCard title={`${isEdited ? 'Edit' : 'Add'} Listings Analytics`}>
            <Formik
                enableReinitialize
                initialValues={{
                    clientPortals: hitCounters[0]?.clientPortals || '',
                    clientViews: hitCounters[0]?.clientViews || '',
                    clientFavorites: hitCounters[0]?.clientFavorites || '',
                    clientPossibilities: hitCounters[0]?.clientPossibilities || '',
                    autoEmails: hitCounters[0]?.autoEmails || '',
                    prev14DayViews: hitCounters[0]?.prev14DayViews || '',
                    agentPublished: hitCounters[0]?.agentPublished || '',
                    agentRejected: hitCounters[0]?.agentRejected || '',
                    agentIDX: hitCounters[0]?.agentIDX || '',
                    agentViews: hitCounters[0]?.agentViews || '',
                    startDate: hitCounters[0]?.startDate ? new Date(hitCounters[0]?.startDate) || null : null
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        const newFields = {
                            // Add the id field based on the value of edit
                            id: isEdited ? hitCounters[0]?.id : undefined,
                            clientPortals: values?.clientPortals || 0,
                            clientViews: values?.clientViews || 0,
                            clientFavorites: values?.clientFavorites || 0,
                            clientPossibilities: values?.clientPossibilities || 0,
                            autoEmails: values?.autoEmails || 0,
                            prev14DayViews: values?.prev14DayViews || 0,
                            agentPublished: values?.agentPublished || 0,
                            agentRejected: values?.agentRejected || 0,
                            agentIDX: values?.agentIDX || 0,
                            agentViews: values?.agentViews || 0,
                            startDate: values?.startDate ? new Date(values.startDate).toISOString() : undefined
                        };

                        let res = await dispatch(addListingAnalytics({ newFields, propertyId }));

                        if (res?.payload) {
                            dispatch(
                                openSnackbar({
                                    open: true,
                                    message: `Analytics saved successfully`,
                                    variant: 'alert',
                                    alert: {
                                        color: 'success'
                                    },
                                    close: true
                                })
                            );

                            historyRefresh();

                            setStatus({ success: true });
                            setSubmitting(false);
                        } else {
                            dispatch(
                                openSnackbar({
                                    open: true,
                                    message:
                                        findKeyInObject(res, `message`) || findKeyInObject(res, `error`) || `Oops! Something went wrong.`,
                                    variant: 'alert',
                                    alert: {
                                        color: 'error'
                                    },
                                    close: true
                                })
                            );
                        }
                    } catch (err) {
                        setStatus({ success: false });
                        setErrors({ submit: 'Invalid credentials' });
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, handleSubmit, handleChange, handleBlur, setFieldValue, isSubmitting, values, touched }) => {
                    return (
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                {/* Client Portals */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="clientPortals"
                                        label="Client Portals"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.clientPortals}
                                        error={touched.clientPortals && errors.clientPortals}
                                        helperText={<ErrorMessage name="clientPortals" />}
                                    />
                                </Grid>
                                {/* Client Views */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="clientViews"
                                        label="Client Views"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.clientViews}
                                        error={touched.clientViews && errors.clientViews}
                                        helperText={<ErrorMessage name="clientViews" />}
                                    />
                                </Grid>

                                {/* Client Favorites */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="clientFavorites"
                                        label="Client Favorites"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.clientFavorites}
                                        error={touched.clientFavorites && errors.clientFavorites}
                                        helperText={<ErrorMessage name="clientFavorites" />}
                                    />
                                </Grid>

                                {/* Client Possibilities */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="clientPossibilities"
                                        label="Client Possibilities"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.clientPossibilities}
                                        error={touched.clientPossibilities && errors.clientPossibilities}
                                        helperText={<ErrorMessage name="clientPossibilities" />}
                                    />
                                </Grid>

                                {/* Auto Emails */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="autoEmails"
                                        label="Auto Emails"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.autoEmails}
                                        error={touched.autoEmails && errors.autoEmails}
                                        helperText={<ErrorMessage name="autoEmails" />}
                                    />
                                </Grid>

                                {/* Previous 14-Day Views */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="prev14DayViews"
                                        label="Previous 14-Day Views"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.prev14DayViews}
                                        error={touched.prev14DayViews && errors.prev14DayViews}
                                        helperText={<ErrorMessage name="prev14DayViews" />}
                                    />
                                </Grid>

                                {/* Agent Published */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="agentPublished"
                                        label="Agent Published"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.agentPublished}
                                        error={touched.agentPublished && errors.agentPublished}
                                        helperText={<ErrorMessage name="agentPublished" />}
                                    />
                                </Grid>

                                {/* Agent Rejected */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="agentRejected"
                                        label="Agent Rejected"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.agentRejected}
                                        error={touched.agentRejected && errors.agentRejected}
                                        helperText={<ErrorMessage name="agentRejected" />}
                                    />
                                </Grid>

                                {/* Agent IDX */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="agentIDX"
                                        label="Agent IDX"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.agentIDX}
                                        error={touched.agentIDX && errors.agentIDX}
                                        helperText={<ErrorMessage name="agentIDX" />}
                                    />
                                </Grid>

                                {/* Agent Views */}
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        type="number"
                                        name="agentViews"
                                        label="Agent Views"
                                        variant="outlined"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.agentViews}
                                        error={touched.agentViews && errors.agentViews}
                                        helperText={<ErrorMessage name="agentViews" />}
                                    />
                                </Grid>

                                {/* DateTime */}
                                <Grid item xs={12} sm={4}>
                                    <DateTimePicker
                                        required
                                        label="Analytics Date"
                                        value={values.startDate}
                                        onChange={(newValue) => {
                                            setFieldValue('startDate', newValue);
                                        }}
                                        onBlur={handleBlur}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                error: Boolean(touched.startDate && errors.startDate),
                                                helperText: touched.startDate && errors.startDate ? 'Analytics Date is Required' : ''
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ m: 2, mt: 4 }}>
                                <AnimateButton>
                                    <LoadingButton
                                        disableElevation
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        loading={isSubmitting}
                                        onClick={() => {
                                            handleSubmit();
                                        }}
                                    >
                                        {`${isEdited ? 'Edit' : 'Save'} Analytics`}
                                    </LoadingButton>
                                </AnimateButton>
                            </Grid>
                        </Grid>
                    );
                }}
            </Formik>
        </MainCard>
    );
}
